// English UI dictionary for the QR Phone caller flow and staff softphone.
//
// This is the single source of truth for every user-facing string in the app
// (titles, subtitles, buttons, placeholders, status messages, toasts, aria-labels,
// and the user-visible call-status texts produced by the signaling core).
//
// Parameterised strings are exposed as functions (e.g. queuePosition) so callers
// pass the runtime value and the wording — including word order — stays in this file
// per language.
//
// This file also defines the `Dictionary` shape every other language must satisfy
// (see es.ts): same keys, same function signatures. The e2e suite extracts its
// expected strings straight from this file (e2e/get_strings.cjs), so keys must not
// be renamed or removed casually — values may change freely.
export const en = {
  // BRAND
  brand: 'Roommatik QR Phone',

  // ---------- LANGUAGE SWITCH (ScanLanding, StaffDashboard) ----------
  // The two language names are proper nouns and stay identical in every dictionary.
  languageSwitchLabel: 'Language',
  languageSpanish: 'Español',
  languageEnglish: 'English',

  // ---------- CALLER: LANDING + FORM (ScanLanding) ----------
  landingDefaultTitle: 'Video call',
  landingIntro: 'A member of our team will assist you by video call in just a moment.',
  startCall: 'Start call',
  loading: 'Loading…',

  // ---------- CALLER: VIRTUAL ASSISTANT (ScanLanding choice, StaffNotAvailable) ----------
  // Never name a provider or a technology: it is "our virtual assistant".
  assistantChoiceIntro: 'How would you like us to help you?',
  assistantTalk: 'Talk to our virtual assistant',
  assistantCallReception: 'Call reception',
  assistantVoiceNote: 'You can talk to the virtual assistant or write to it. If you talk, your browser will ask for access to your microphone.',
  assistantFallbackIntro: 'In the meantime, our virtual assistant can help you right away.',
  assistantOpening: 'Opening the assistant…',
  assistantUnavailable: 'The assistant is not available at the moment. You can call reception or leave us your details.',

  // CALLER: FORM VALIDATION (ScanLanding, StaffNotAvailable)
  fieldRequired: 'Please enter this information to continue.',
  invalidEmail: 'Please enter a valid email address.',
  invalidPhone: 'Please enter a valid phone number.',

  // CALLER: PERMISSIONS (ScanLanding)
  preparingCall: 'Preparing your call…',
  permissionsTitle: 'Permissions required',
  permissionsIntro: 'To assist you, we need access to your microphone and camera. Your browser will ask you to confirm.',
  requestingPermissions: 'Requesting permissions…',
  allowAndContinue: 'Allow and continue',
  back: 'Back',
  permissionsDenied:
    'We could not access your camera and microphone. Please enable the permissions in your browser (the lock icon next to the address) and try again.',

  // CALLER: EXPIRED / INVALID / UNAVAILABLE (ScanLanding, InvalidQr)
  qrExpiredTitle: 'This code is no longer valid',
  qrExpiredIntro:
    'The code you scanned has expired or is not correct. Please try again later or ask a member of staff at the venue.',
  // A short-lived link shown on a screen (kiosk help): the backend says why it was refused.
  linkExpiredTitle: 'This link has expired',
  linkExpiredIntro: 'Codes shown on the screen only work for a few minutes. Please scan the code on the screen again.',
  linkUsedTitle: 'This link has already been used',
  linkUsedIntro: 'Each code shown on the screen works only once. Please ask for a new code on the screen and scan it.',
  invalidQrTitle: 'Invalid code',
  invalidQrIntro:
    'The code you scanned is not valid. Please scan the code again or ask a member of staff at the venue.',

  // CALLER: SITE ROOT (RootLanding, roadmap #108)
  rootLandingTitle: 'Video call with reception',
  rootLandingIntro:
    'This page opens by scanning a room QR code or pressing the call button on a Roommatik panel. Please try again from there.',
  rootLandingStaffLink: 'Staff access',

  serviceUnavailableTitle: 'Service unavailable',
  serviceUnavailablePrepare:
    'We could not set up the call. Please check your internet connection and try again in a few moments.',
  serviceInactive: 'This service is not active at the moment. Please try again later.',
  notYetAvailableTitle: 'Not yet available',
  availableFrom: (when: string) => `This service will be available from ${when}.`,
  retry: 'Try again',

  // ---------- CALLER: IN-CALL / QUEUE (CallView, CallScreen) ----------
  checkConnection: 'Please check your internet connection.',
  connectingCall: 'We are connecting your call. Please wait…',
  queuePosition: (n: number | string) => `Position in queue: ${n}`,
  cancel: 'Cancel',
  beingAssisted: 'You are being assisted',
  cameraOff: 'Camera off',
  unstableConnection: 'Unstable connection. Trying to recover the call…',
  micOff: 'Microphone off',

  // CALLER: NO STAFF (CallView failure panel)
  cannotAssistTitle: 'We were unable to assist you',
  allBusy: 'All of our team members are busy right now. Please try again in a few minutes.',

  // ---------- CALLER: CALL ENDED (CallEnded) ----------
  callEndedTitle: 'Call ended',
  callDuration: (formatted: string) => `Duration: ${formatted}`,
  thanksForUsing: 'Thank you for using this service.',
  needAnythingElse: 'Do you need anything else?',
  callAgain: 'Call again',
  youMayCloseScreen: 'You may now close this screen.',

  // ---------- CALLER: CALL ERROR (CallError) ----------
  callErrorTitle: 'We could not complete the call',
  callErrorIntro: 'There was a problem during the call. Please try again in a few minutes.',

  // ---------- CALLER: CALLBACK / NO STAFF (StaffNotAvailable) ----------
  callbackIntro:
    'All of our team members are busy at the moment. If you wish, leave us a contact detail and we will get back to you as soon as possible.',
  callbackWantContact: 'Would you like us to contact you?',
  yes: 'Yes',
  no: 'No',
  callbackDeclined: 'That is fine. We hope to assist you soon. You may now close this screen.',
  callbackPreferredMethod: 'How would you prefer us to contact you?',
  emailLabel: 'Email address',
  phoneLabel: 'Phone number',
  send: 'Send',
  callbackThanks:
    'Thank you. We have registered your details and will get back to you as soon as possible.',
  // Shown instead of the contact form on public kiosks (no keyboard).
  callbackKioskMessage:
    'All of our team members are busy at the moment. Please try again shortly, or ask a member of staff at the venue.',

  // ---------- CONTINUE THE CALLBACK ON THE GUEST'S OWN PHONE (#61) ----------
  // Offered on keyboard-less public screens: the code opens the form below on the
  // guest's own phone, where they do have a keyboard.
  callbackScanTitle: 'Prefer to leave your details?',
  callbackScanIntro: 'Scan this code with your phone to leave them there, using your own keyboard.',
  callbackScanAlt: 'Code to continue on your phone',
  callbackPhoneTitle: 'Leave your contact details',
  callbackPhoneIntro: 'Nobody was able to take your call. Leave your details and we will get back to you.',
  callbackPhoneInvalidLink: 'This link is incomplete. Please scan the code on the screen again.',

  // ---------- SHARED IN-CALL CONTROL BAR (CallControls aria-labels) ----------
  muteMic: 'Mute microphone',
  unmuteMic: 'Unmute microphone',
  turnOffCamera: 'Turn off camera',
  turnOnCamera: 'Turn on camera',
  switchCamera: 'Switch camera',
  hangUp: 'Hang up',

  // ---------- STAFF: LOGIN (StaffLogin) ----------
  staffLoginTitle: 'Staff access',
  staffLoginIntro: 'Enter your details to receive calls on this device.',
  staffRedirecting: 'Redirecting to sign-in…',
  passwordLabel: 'Password',
  signingIn: 'Signing in…',
  signIn: 'Sign in',
  loginFailedGeneric: 'We could not sign you in. Please try again.',
  loginFailedCheckDetails: 'We could not sign you in. Please check your details and try again.',
  loginFailedService: 'We could not connect to the service. Please try again.',
  // STAFF: SIGNED IN ELSEWHERE (single-device prompt)
  otherDeviceTitle: 'Already signed in elsewhere',
  otherDeviceBody:
    'This account is currently in use on another device. To continue here, the other device will be signed out.',
  otherDeviceConfirm: 'Continue here',
  otherDeviceCancel: 'Cancel',
  // STAFF: JUST SIGNED OUT (the login screen must not sign them straight back in)
  staffSignedOutTitle: 'You have signed out',
  staffSignedOutBody: 'Your session on this device is closed. Sign in again to receive calls.',

  // ---------- STAFF: DASHBOARD (StaffDashboard) ----------
  staffAvailable: 'Available for calls',
  staffReconnecting: 'Reconnecting…',
  staffNotReachable: 'Call alerts are off',
  staffNotReachableHint: 'While call alerts are off, calls will not reach you on this device.',
  staffAvailableHint: 'Keep this screen open. We will let you know when you receive a call.',
  staffReconnectingHint: 'We are restoring the connection. Please do not close this screen.',
  staffNotificationsHelp:
    'To receive call alerts while this screen is in the background, enable notifications.',
  staffEnableNotifications: 'Enable alerts',
  staffNotificationsBlocked:
    'This browser is blocking notifications for this site, so calls cannot ring on this device. Allow notifications from the padlock icon in the address bar and press the button again. Private and guest windows cannot receive call alerts: use a normal window.',
  staffNotificationsUnsupported:
    'This browser cannot receive call alerts. Use an up-to-date Chrome, Edge or Firefox in a normal window, or install the application.',
  staffNotificationsDismissed:
    'The question was closed without an answer. Press the button again and choose Allow.',
  staffNotificationsFailed:
    'Notifications are allowed, but this device could not be registered for call alerts. Check the connection and try again.',
  staffRetryNotifications:
    'Try again',
  staffNotificationsEnabled:
    'Call alerts are enabled on this device.',
  staffLogout: 'Sign out',
  // Shown when a notification is acted on after its call is already over.
  staffCallNoLongerActive: 'That call is no longer active.',

  // ---------- STAFF: INSTALL APPLICATION (StaffDashboard, roadmap #100) ----------
  // These used to be the sanctioned bilingual-inline exception (Spanish / English in
  // one string) while the app had a single dictionary; with runtime language support
  // they are plain single-language strings like everything else. Naming the user's
  // own browsers (Microsoft Edge / Google Chrome) remains sanctioned.
  installAppButton: 'Install application',
  installAppHint: 'Add this application to your desktop to receive calls faster.',
  installFallbackTitle: 'Installation not available',
  installFallbackBody:
    'This browser cannot install the application. To install it on your desktop, open this page with Microsoft Edge or Google Chrome.',
  installFallbackClose: 'Close',

  // ---------- STAFF: DESKTOP SETUP (StaffDashboard, roadmap #100 second follow-up) ----------
  // Sequential disclosure per operator feedback: only step 1 (install) shows until the
  // app reports as installed, unnumbered since there is no visible second step yet;
  // step 2 (auto-start) then appears, numbered alongside a collapsed step-1 confirmation.
  desktopSetupHeading: 'Desktop setup',
  desktopSetupInstallLabel: 'Install application',
  desktopSetupInstallHint:
    'No file is downloaded: your browser will ask for confirmation and the application will open as its own window.',
  desktopSetupInstalled: '✓ Application installed',
  desktopSetupStep2Label: 'Enable automatic start',
  desktopSetupStep2Hint:
    'Download the file and double-click it: the window will open by itself when you sign in and calls will keep ringing even if you close it.',
  desktopSetupDownload: 'Download setup file',
  // Step-2 lifecycle states (pending → downloaded → confirmed → verified), tracked
  // in staff/setupState.ts. 'verified' means a window launch carried the
  // ?autostart=1 marker the setup file plants in the Startup shortcut.
  desktopSetupDownloaded: 'File downloaded: double-click it and accept the system prompt.',
  desktopSetupConfirmRun: 'I have run it',
  desktopSetupDownloadAgain: 'Download again',
  desktopSetupConfirmed:
    '✓ Automatic start enabled; it will be checked automatically on your next sign-in.',
  desktopSetupVerified: '✓ Automatic start enabled and verified.',

  // ---------- STAFF: IN-CALL (StaffCallView) ----------
  staffDefaultCaller: 'Caller',
  staffConnectingCall: 'Connecting the call…',
  staffCustomerMicOff: 'Caller microphone off',

  // ---------- STAFF: INCOMING CALL (StaffIncomingCall) ----------
  staffNoActiveCall: 'There is no active call.',
  staffNoIncomingCall: 'There is no incoming call at the moment.',
  staffIncomingTitle: 'Incoming call',
  staffReject: 'Decline',
  staffAccept: 'Accept',
  // Operating-system notification body when the softphone window is not in front.
  staffIncomingNotificationBody: (who: string) => `${who} is calling you.`,
  staffAlreadyAnswered: 'Another team member has already taken this call.',

  // ---------- PUBLIC STATIC / LEGAL PAGES (rehosted from the legacy frontend) ----------
  // Page chrome only. The verbatim legal bodies live inline in their components.
  privacyPolicyTitle: 'Privacy Policy',
  deleteAccountTitle: 'How to delete account',

  // CONTACT PAGE (Contact) → POST /users/contactMail
  contactTitle: 'Contact us',
  contactIntro: 'Send us your query and we will get back to you as soon as possible.',
  contactNameLabel: 'Name',
  contactMessageLabel: 'Message',
  contactPrivacyConsent: 'I have read and agree to the',
  contactPrivacyRequired: 'Please accept the privacy policy to continue.',
  contactPromotionConsent: 'I would like to receive commercial information and promotional offers by email.',
  contactSubmit: 'Submit',
  contactSending: 'Please wait…',
  contactSuccess: 'Thank you. Your message has been sent.',
  contactError: 'We could not send your message. Please try again.',

  // LANDING PAGE (Landing)
  landingHeroTitle: 'Scan, connect, video call',
  landingHeroSubtitle: 'Instant video calls with a simple QR code scan.',
  landingCard1Title: 'For personal use',
  landingCard1Body: 'Share a QR code, not your phone number. You can activate and deactivate your QR codes at any time.',
  landingCard2Title: 'For companies',
  landingCard2Body: 'Invite your staff and create schedules. Give a human touch to your customers.',
  landingCard3Title: 'No downloads needed',
  landingCard3Body: 'No app is required for callers. By scanning the QR code, a web page with the call opens up.',

  // ---------- CALLER: USER-VISIBLE CALL-STATUS TEXTS (core/VideoState) ----------
  statusConnecting: 'We are connecting your call. Please wait…',
  statusCalling: 'Calling…',
  statusWillAssistShortly: 'We will assist you in a moment…',
  statusEveryoneBusy:
    'All of our team members are assisting other calls. Please do not close this screen; we will attend to you as soon as possible.',
  cameraActivationFailed: 'We could not turn on the camera. Please check your connection and camera permissions.',
}

// No `as const`: the values must type as plain strings (and string-returning
// functions) so other dictionaries (es.ts) can satisfy the same shape.
export type Dictionary = typeof en
